import React, { useState, useEffect } from 'react';
import { 
  Users, 
  FileText, 
  Award, 
  DollarSign, 
  BarChart3, 
  Settings, 
  LogOut,
  Plus,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Globe,
  ChevronDown,
  ChevronRight,
  Search,
  Filter,
  X,
  Calendar,
  Mail,
  Phone,
  Download,
  FileText as FileTextIcon
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import CountryMaster from './CountryMaster';
import StateMaster from './StateMaster';
import CityMaster from './CityMaster';
import BusinessCategoryMaster from './BusinessCategoryMaster';
import SettingsManagement from './SettingsManagement';
import ApplicationPeriodsManagement from './ApplicationPeriodsManagement';
import FundingResultsManagement from './FundingResultsManagement';
import './AdminPanel.css';
import { Base_Url, API_ENDPOINTS } from '../config/domainConfig.js';

const AdminPanel = () => {
  const { showSuccess, showError } = useToast();
  const API_BASE_URL = `${Base_Url}/api`;
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [mastersMenuOpen, setMastersMenuOpen] = useState(false);
  
  // Dashboard data
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    totalApplications: 0,
    pendingApplications: 0,
    successfulPayments: 0,
    totalAmount: 0
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);

  // Users management
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [userSearch, setUserSearch] = useState('');

  // Applications management
  const [applications, setApplications] = useState([]);
  const [applicationsPagination, setApplicationsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [applicationSearch, setApplicationSearch] = useState('');
  const [applicationStatusFilter, setApplicationStatusFilter] = useState('');

  // Payments management
  const [payments, setPayments] = useState([]);
  const [paymentsPagination, setPaymentsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [paymentStats, setPaymentStats] = useState({});

  // Applicants without payments report
  const [applicantsWithoutPayments, setApplicantsWithoutPayments] = useState([]);
  const [applicantsWithoutPaymentsPagination, setApplicantsWithoutPaymentsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [applicantsWithoutPaymentsSearch, setApplicantsWithoutPaymentsSearch] = useState('');

  // Application details modal
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [statusUpdateForm, setStatusUpdateForm] = useState({
    status: '',
    admin_notes: ''
  });

  // User management modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'user',
    status: 'active'
  });
  const [userFormErrors, setUserFormErrors] = useState({});
  const [isEditingUser, setIsEditingUser] = useState(false);



  // Video player modal
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // PDF viewer modal
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);



  // API Functions
  const fetchDashboardStats = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(API_ENDPOINTS.DASHBOARD_STATS);
      const data = await response.json();
      
      if (data.success) {
        setDashboardStats(data.data.stats);
        setRecentApplications(data.data.recentApplications);
        setRecentPayments(data.data.recentPayments);
      } else {
        showError(data.message || 'Failed to fetch dashboard stats');
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      showError('Failed to fetch dashboard statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async (page = 1, search = '') => {
    try {
      const response = await fetch(`${API_ENDPOINTS.USERS}?page=${page}&search=${search}`);
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.data.users);
        setUsersPagination(data.data.pagination);
      } else {
        showError(data.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showError('Failed to fetch users');
    }
  };

  const fetchApplications = async (page = 1, status = '', search = '') => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/applications?page=${page}&status=${status}&search=${search}`);
      const data = await response.json();
      
      if (data.success) {
        setApplications(data.data.applications);
        setApplicationsPagination(data.data.pagination);
      } else {
        showError(data.message || 'Failed to fetch applications');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      showError('Failed to fetch applications');
    }
  };

  const fetchPayments = async (page = 1, status = '') => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/payments?page=${page}&status=${status}`);
      const data = await response.json();
      
      if (data.success) {
        setPayments(data.data.payments);
        setPaymentsPagination(data.data.pagination);
        setPaymentStats(data.data.stats);
      } else {
        showError(data.message || 'Failed to fetch payments');
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      showError('Failed to fetch payments');
    }
  };

  const fetchApplicantsWithoutPayments = async (page = 1, search = '') => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      if (search) params.append('search', search);
      
      const response = await fetch(`${API_BASE_URL}/admin/applicants-without-payments?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setApplicantsWithoutPayments(data.data.applicants);
        setApplicantsWithoutPaymentsPagination(data.data.pagination);
      } else {
        showError(data.message || 'Failed to fetch applicants without payments');
      }
    } catch (error) {
      console.error('Error fetching applicants without payments:', error);
      showError('Failed to fetch applicants without payments');
    }
  };

  const exportApplicantsWithoutPayments = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      
      if (applicantsWithoutPaymentsSearch) {
        params.append('search', applicantsWithoutPaymentsSearch);
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/export-applicants-without-payments?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Get filename from response headers
        const contentDisposition = response.headers.get('content-disposition');
        let filename = 'applicants-without-payments.xlsx';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        showSuccess('Excel file downloaded successfully!');
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to export data');
      }
    } catch (error) {
      console.error('Error exporting applicants without payments:', error);
      showError('Failed to export data');
    }
  };

  const exportApplications = async (format = 'excel') => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      
      if (applicationSearch) {
        params.append('search', applicationSearch);
      }
      
      if (applicationStatusFilter) {
        params.append('status', applicationStatusFilter);
      }
      
      const endpoint = format === 'pdf' ? 'export-applications-pdf' : 'export-applications';
      const response = await fetch(`${API_BASE_URL}/admin/${endpoint}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Get filename from response headers
        const contentDisposition = response.headers.get('content-disposition');
        let filename = `applications.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        showSuccess(`${format.toUpperCase()} file downloaded successfully!`);
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to export data');
      }
    } catch (error) {
      console.error(`Error exporting applications to ${format}:`, error);
      showError('Failed to export data');
    }
  };

  // User management functions
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      status: user.status
    });
    setIsEditingUser(true);
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        if (data.success) {
          showSuccess('User deleted successfully');
          fetchUsers(usersPagination.page, userSearch);
        } else {
          showError(data.message || 'Failed to delete user');
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        showError('Failed to delete user');
      }
    }
  };

  const handleUserFormSubmit = async (e) => {
    e.preventDefault();
    setUserFormErrors({});

    // Basic validation
    const errors = {};
    if (!userForm.name.trim()) errors.name = 'Name is required';
    if (!userForm.email.trim()) errors.email = 'Email is required';
    if (!userForm.phone.trim()) errors.phone = 'Phone is required';

    if (Object.keys(errors).length > 0) {
      setUserFormErrors(errors);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const url = isEditingUser 
        ? `${API_BASE_URL}/admin/users/${selectedUser.id}`
        : `${API_BASE_URL}/admin/users`;
      
      const response = await fetch(url, {
        method: isEditingUser ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userForm)
      });

      const data = await response.json();
      if (data.success) {
        showSuccess(isEditingUser ? 'User updated successfully' : 'User created successfully');
        setShowUserModal(false);
        setSelectedUser(null);
        setUserForm({
          name: '',
          email: '',
          phone: '',
          role: 'user',
          status: 'active'
        });
        setIsEditingUser(false);
        fetchUsers(usersPagination.page, userSearch);
      } else {
        showError(data.message || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      showError('Failed to save user');
    }
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
    setUserForm({
      name: '',
      email: '',
      phone: '',
      role: 'user',
      status: 'active'
    });
    setUserFormErrors({});
    setIsEditingUser(false);
  };

  const updateApplicationStatus = async (applicationId, status, adminNotes) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/applications/${applicationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, admin_notes: adminNotes }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSuccess('Application status updated successfully');
        // Refresh applications list
        fetchApplications(applicationsPagination.page, applicationStatusFilter, applicationSearch);
        // Close modal
        setShowApplicationModal(false);
        setSelectedApplication(null);
      } else {
        showError(data.message || 'Failed to update application status');
      }
    } catch (error) {
      console.error('Error updating application status:', error);
      showError('Failed to update application status');
    }
  };

  const handleVideoView = (videoFile) => {
    setSelectedVideo(videoFile);
    setShowVideoModal(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setSelectedVideo(null);
  };

  const handleVideoError = (event) => {
    console.error('Video playback error:', event);
    // You can add a toast notification here if needed
  };

  const handlePdfView = (pdfFile) => {
    setSelectedPdf(pdfFile);
    setShowPdfModal(true);
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
    setSelectedPdf(null);
  };




  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    // Show success message
    showSuccess('Logged out successfully!');
    // Redirect to home page
    window.location.href = '/';
  };

  useEffect(() => {
    // Fetch data based on active tab
    switch (activeTab) {
      case 'dashboard':
        fetchDashboardStats();
        break;
      case 'users':
        fetchUsers(1, userSearch);
        break;
      case 'applications':
        fetchApplications(1, applicationStatusFilter, applicationSearch);
        break;
      case 'payments':
        fetchPayments(1, paymentStatusFilter);
        break;
      case 'payment-reports':
        fetchApplicantsWithoutPayments(1, applicantsWithoutPaymentsSearch);
        break;
    }
  }, [activeTab]);

  // Fetch data when filters change
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers(1, userSearch);
    }
  }, [userSearch]);

  useEffect(() => {
    if (activeTab === 'applications') {
      fetchApplications(1, applicationStatusFilter, applicationSearch);
    }
  }, [applicationStatusFilter, applicationSearch]);

  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPayments(1, paymentStatusFilter);
    }
  }, [paymentStatusFilter]);

  useEffect(() => {
    if (activeTab === 'payment-reports') {
      fetchApplicantsWithoutPayments(1, applicantsWithoutPaymentsSearch);
    }
  }, [applicantsWithoutPaymentsSearch]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'reviewing':
        return 'info';
      case 'inactive':
      case 'rejected':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
      case 'approved':
        return <CheckCircle size={16} />;
      case 'pending':
        return <Clock size={16} />;
      case 'reviewing':
        return <Eye size={16} />;
      case 'inactive':
      case 'rejected':
        return <XCircle size={16} />;
      default:
        return null;
    }
  };

  const Dashboard = () => (
    <div className="dashboard">
      <h2>Dashboard Overview</h2>
      
      {isLoading ? (
        <div className="loading">Loading statistics...</div>
      ) : (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon users">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <h3>{dashboardStats.totalUsers}</h3>
              <p>Total Users</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon applications">
              <FileText size={24} />
            </div>
            <div className="stat-content">
              <h3>{dashboardStats.totalApplications}</h3>
              <p>Total Applications</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon pending">
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <h3>{dashboardStats.pendingApplications}</h3>
              <p>Pending Applications</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon success">
              <DollarSign size={24} />
            </div>
            <div className="stat-content">
              <h3>₹{dashboardStats.totalAmount.toLocaleString()}</h3>
              <p>Total Revenue</p>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-sections">
        <div className="recent-applications">
          <h3>Recent Applications</h3>
        <div className="activity-list">
            {recentApplications.length > 0 ? (
              recentApplications.map(app => (
                <div key={app.id} className="activity-item">
            <div className="activity-icon">
              <FileText size={16} />
            </div>
            <div className="activity-content">
                    <p>New application from {app.name}</p>
                    <span className="activity-time">
                      {new Date(app.created_at).toLocaleDateString()}
                    </span>
            </div>
          </div>
              ))
            ) : (
              <p className="no-data">No recent applications</p>
            )}
            </div>
          </div>
          
        <div className="recent-payments">
          <h3>Recent Payments</h3>
          <div className="activity-list">
            {recentPayments.length > 0 ? (
              recentPayments.map(payment => (
                <div key={payment.id} className="activity-item">
            <div className="activity-icon">
                    <DollarSign size={16} />
            </div>
            <div className="activity-content">
                    <p>Payment of ₹{payment.amount} from {payment.applicant_name}</p>
                    <span className="activity-time">
                      {new Date(payment.created_at).toLocaleDateString()}
                    </span>
            </div>
                </div>
              ))
            ) : (
              <p className="no-data">No recent payments</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const UsersManagement = () => (
    <div className="users-management">
      <div className="section-header">
        <h2>Users Management</h2>
        <div className="search-section">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map(user => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>{user.name}</td>
                <td>{user.email}</td>
                  <td>{user.phone}</td>
                <td>
                  <span className={`badge ${user.role === 'admin' ? 'admin' : 'user'}`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${getStatusColor(user.status)}`}>
                    {getStatusIcon(user.status)}
                    {user.status}
                  </span>
                </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-icon" 
                      title="Edit"
                      onClick={() => handleEditUser(user)}
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="btn-icon" 
                      title="Delete"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {usersPagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => fetchUsers(usersPagination.page - 1, userSearch)}
            disabled={usersPagination.page === 1}
          >
            Previous
          </button>
          <span>Page {usersPagination.page} of {usersPagination.pages}</span>
          <button 
            onClick={() => fetchUsers(usersPagination.page + 1, userSearch)}
            disabled={usersPagination.page === usersPagination.pages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  const ApplicationsManagement = () => (
    <div className="applications-management">
      <div className="section-header">
        <div className="header-content">
          <h2>Applications Management</h2>
          <div className="export-buttons">
            <button 
              className="export-btn"
              onClick={() => exportApplications('excel')}
              title="Export to Excel"
            >
              <Download size={16} />
              Export to Excel
            </button>
            <button 
              className="export-btn pdf"
              onClick={() => exportApplications('pdf')}
              title="Export to PDF"
            >
              <FileTextIcon size={16} />
              Export to PDF
            </button>
          </div>
        </div>
        <div className="filters-section">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search applications..."
              value={applicationSearch}
              onChange={(e) => setApplicationSearch(e.target.value)}
            />
          </div>
        <div className="filter-buttons">
            <button 
              className={`btn-filter ${applicationStatusFilter === '' ? 'active' : ''}`}
              onClick={() => setApplicationStatusFilter('')}
            >
              All
            </button>
            <button 
              className={`btn-filter ${applicationStatusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setApplicationStatusFilter('pending')}
            >
              Pending
            </button>
            <button 
              className={`btn-filter ${applicationStatusFilter === 'reviewing' ? 'active' : ''}`}
              onClick={() => setApplicationStatusFilter('reviewing')}
            >
              Reviewing
            </button>
            <button 
              className={`btn-filter ${applicationStatusFilter === 'approved' ? 'active' : ''}`}
              onClick={() => setApplicationStatusFilter('approved')}
            >
              Approved
            </button>
            <button 
              className={`btn-filter ${applicationStatusFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setApplicationStatusFilter('rejected')}
            >
              Rejected
            </button>
          </div>
        </div>
      </div>
      
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Applicant</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Category</th>
              <th>Payment Status</th>
              <th>Application Status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.length > 0 ? (
              applications.map(app => (
              <tr key={app.id}>
                <td>{app.id}</td>
                <td>{app.name}</td>
                <td>{app.email}</td>
                  <td>{app.phone}</td>
                  <td>{app.category}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(app.payment_status || 'none')}`}>
                      {app.payment_status || 'No Payment'}
                    </span>
                  </td>
                <td>
                  <span className={`status-badge ${getStatusColor(app.status)}`}>
                    {getStatusIcon(app.status)}
                    {app.status}
                  </span>
                </td>
                  <td>{new Date(app.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                      <button 
                        className="btn-icon" 
                        title="View Details"
                        onClick={() => {
                          setSelectedApplication(app);
                          setShowApplicationModal(true);
                        }}
                      >
                      <Eye size={16} />
                    </button>

                      <button 
                        className="btn-icon" 
                        title="Approve"
                        onClick={() => updateApplicationStatus(app.id, 'approved', '')}
                      >
                      <CheckCircle size={16} />
                    </button>
                      <button 
                        className="btn-icon" 
                        title="Reject"
                        onClick={() => updateApplicationStatus(app.id, 'rejected', '')}
                      >
                      <XCircle size={16} />
                    </button>
                  </div>
                </td>
              </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="no-data">No applications found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {applicationsPagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => fetchApplications(applicationsPagination.page - 1, applicationStatusFilter, applicationSearch)}
            disabled={applicationsPagination.page === 1}
          >
            Previous
          </button>
          <span>Page {applicationsPagination.page} of {applicationsPagination.pages}</span>
          <button 
            onClick={() => fetchApplications(applicationsPagination.page + 1, applicationStatusFilter, applicationSearch)}
            disabled={applicationsPagination.page === applicationsPagination.pages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );



  const PaymentsManagement = () => (
    <div className="payments-management">
      <div className="section-header">
        <h2>Payments Management</h2>
        <div className="filters-section">
          <div className="filter-buttons">
            <button 
              className={`btn-filter ${paymentStatusFilter === '' ? 'active' : ''}`}
              onClick={() => setPaymentStatusFilter('')}
            >
              All
        </button>
            <button 
              className={`btn-filter ${paymentStatusFilter === 'success' ? 'active' : ''}`}
              onClick={() => setPaymentStatusFilter('success')}
            >
              Successful
                    </button>
            <button 
              className={`btn-filter ${paymentStatusFilter === 'failed' ? 'active' : ''}`}
              onClick={() => setPaymentStatusFilter('failed')}
            >
              Failed
            </button>
            <button 
              className={`btn-filter ${paymentStatusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setPaymentStatusFilter('pending')}
            >
              Pending
                    </button>
                  </div>
      </div>
    </div>

      {paymentStats.total_payments > 0 && (
        <div className="payment-stats">
          <div className="stat-item">
            <span className="stat-label">Total Payments:</span>
            <span className="stat-value">{paymentStats.total_payments}</span>
      </div>
          <div className="stat-item">
            <span className="stat-label">Successful:</span>
            <span className="stat-value success">{paymentStats.successful_payments}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Failed:</span>
            <span className="stat-value danger">{paymentStats.failed_payments}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Pending:</span>
            <span className="stat-value warning">{paymentStats.pending_payments}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Amount:</span>
            <span className="stat-value">₹{parseFloat(paymentStats.total_amount || 0).toLocaleString()}</span>
          </div>
        </div>
      )}
      
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Applicant</th>
              <th>User</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Payment ID</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.length > 0 ? (
              payments.map(payment => (
                <tr key={payment.id}>
                  <td>{payment.id}</td>
                  <td>{payment.applicant_name}</td>
                  <td>{payment.user_name}</td>
                  <td>₹{payment.amount}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(payment.status)}`}>
                      {getStatusIcon(payment.status)}
                      {payment.status}
                  </span>
                </td>
                  <td>{payment.razorpay_payment_id || 'N/A'}</td>
                  <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                      <button className="btn-icon" title="View Details">
                        <Eye size={16} />
                    </button>
                  </div>
                </td>
              </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">No payments found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {paymentsPagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => fetchPayments(paymentsPagination.page - 1, paymentStatusFilter)}
            disabled={paymentsPagination.page === 1}
          >
            Previous
          </button>
          <span>Page {paymentsPagination.page} of {paymentsPagination.pages}</span>
          <button 
            onClick={() => fetchPayments(paymentsPagination.page + 1, paymentStatusFilter)}
            disabled={paymentsPagination.page === paymentsPagination.pages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  const PaymentReports = () => (
    <div className="payment-reports">
      <div className="section-header">
        <h2>Payment Reports</h2>
        <p>Applicants who have submitted applications but haven't completed payments</p>
        <button 
          className="export-btn"
          onClick={exportApplicantsWithoutPayments}
          title="Export to Excel"
        >
          <Download size={16} />
          Export to Excel
        </button>
      </div>

      <div className="search-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={applicantsWithoutPaymentsSearch}
            onChange={(e) => setApplicantsWithoutPaymentsSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Location</th>
              <th>Category</th>
              <th>Application Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applicantsWithoutPayments.length > 0 ? (
              applicantsWithoutPayments.map((applicant) => (
                <tr key={applicant.id}>
                  <td>{applicant.name}</td>
                  <td>
                    <a href={`mailto:${applicant.email}`} className="email-link">
                      {applicant.email}
                    </a>
                  </td>
                  <td>
                    <a href={`tel:${applicant.phone}`} className="phone-link">
                      {applicant.phone}
                    </a>
                  </td>
                  <td>{`${applicant.city}, ${applicant.state}, ${applicant.country}`}</td>
                  <td>{applicant.category}</td>
                  <td>{new Date(applicant.application_date).toLocaleDateString()}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(applicant.application_status)}`}>
                      {getStatusIcon(applicant.application_status)}
                      {applicant.application_status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn contact"
                        onClick={() => window.open(`mailto:${applicant.email}?subject=Payment Reminder - Vikas Fund Application`, '_blank')}
                        title="Send Email"
                      >
                        <Mail size={16} />
                      </button>
                      <button 
                        className="action-btn contact"
                        onClick={() => window.open(`tel:${applicant.phone}`, '_blank')}
                        title="Call"
                      >
                        <Phone size={16} />
                      </button>
                      <button 
                        className="action-btn view"
                        onClick={() => {
                          // You can add functionality to view application details
                          showSuccess(`Contact ${applicant.name} at ${applicant.email} or ${applicant.phone}`);
                        }}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">No applicants without payments found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {applicantsWithoutPaymentsPagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => fetchApplicantsWithoutPayments(applicantsWithoutPaymentsPagination.page - 1, applicantsWithoutPaymentsSearch)}
            disabled={applicantsWithoutPaymentsPagination.page === 1}
          >
            Previous
          </button>
          <span>Page {applicantsWithoutPaymentsPagination.page} of {applicantsWithoutPaymentsPagination.pages}</span>
          <button 
            onClick={() => fetchApplicantsWithoutPayments(applicantsWithoutPaymentsPagination.page + 1, applicantsWithoutPaymentsSearch)}
            disabled={applicantsWithoutPaymentsPagination.page === applicantsWithoutPaymentsPagination.pages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );



  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'users':
        return <UsersManagement />;
      case 'applications':
        return <ApplicationsManagement />;
      case 'payments':
        return <PaymentsManagement />;
      case 'payment-reports':
        return <PaymentReports />;
      case 'country-master':
        return <CountryMaster />;
      case 'state-master':
        return <StateMaster />;
      case 'city-master':
        return <CityMaster />;
      case 'business-category-master':
        return <BusinessCategoryMaster />;
      case 'application-periods':
        return <ApplicationPeriodsManagement />;
      case 'funding-results':
        return <FundingResultsManagement />;
      case 'settings':
        return <SettingsManagement />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-sidebar">
        <div className="admin-header">
          <h1>Vikas Fund</h1>
          <p>Admin Panel</p>
        </div>
        
        <nav className="admin-nav">
          <button 
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <BarChart3 size={20} />
            Dashboard
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={20} />
            Users
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'applications' ? 'active' : ''}`}
            onClick={() => setActiveTab('applications')}
          >
            <FileText size={20} />
            Applications
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            <DollarSign size={20} />
            Payments
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'payment-reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('payment-reports')}
          >
            <FileText size={20} />
            Payment Reports
          </button>
          
          {/* Masters Menu */}
          <div className="masters-menu">
            <button 
              className={`nav-item masters-toggle ${['country-master', 'state-master', 'city-master', 'business-category-master'].includes(activeTab) ? 'active' : ''}`}
              onClick={() => setMastersMenuOpen(!mastersMenuOpen)}
            >
              <Globe size={20} />
              <span>Masters</span>
              {mastersMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            
            {mastersMenuOpen && (
              <div className="masters-submenu">
                <button 
                  className={`submenu-item ${activeTab === 'country-master' ? 'active' : ''}`}
                  onClick={() => setActiveTab('country-master')}
                >
                  Country Master
                </button>
                <button 
                  className={`submenu-item ${activeTab === 'state-master' ? 'active' : ''}`}
                  onClick={() => setActiveTab('state-master')}
                >
                  State Master
                </button>
                <button 
                  className={`submenu-item ${activeTab === 'city-master' ? 'active' : ''}`}
                  onClick={() => setActiveTab('city-master')}
                >
                  City Master
                </button>
                <button 
                  className={`submenu-item ${activeTab === 'business-category-master' ? 'active' : ''}`}
                  onClick={() => setActiveTab('business-category-master')}
                >
                  Business Category Master
                </button>
              </div>
            )}
          </div>

          {/* Application Management */}
          <button 
            className={`nav-item ${activeTab === 'application-periods' ? 'active' : ''}`}
            onClick={() => setActiveTab('application-periods')}
          >
            <Calendar size={20} />
            <span>Application Periods</span>
          </button>

          <button 
            className={`nav-item ${activeTab === 'funding-results' ? 'active' : ''}`}
            onClick={() => setActiveTab('funding-results')}
          >
            <Award size={20} />
            <span>Funding Results</span>
          </button>

          {/* Settings */}
          <button 
            className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={20} />
            <span>Settings</span>
          </button>
        </nav>
        
        <div className="admin-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </div>
      
      <div className="admin-content">
        <div className="content-header">
          <h2>{activeTab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</h2>
        </div>
        
        <div className="content-body">
          {renderContent()}
        </div>
      </div>

      {/* Application Details Modal */}
      {showApplicationModal && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowApplicationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Application Details</h2>
              <button className="modal-close" onClick={() => setShowApplicationModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="application-details-grid">
                {/* Personal Information */}
                <div className="detail-section">
                  <h3>Personal Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Full Name:</span>
                      <span className="value">{selectedApplication.name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Email:</span>
                      <span className="value">{selectedApplication.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Phone:</span>
                      <span className="value">{selectedApplication.phone}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Address:</span>
                      <span className="value">{selectedApplication.address}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">City:</span>
                      <span className="value">{selectedApplication.city}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">State:</span>
                      <span className="value">{selectedApplication.state}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Country:</span>
                      <span className="value">{selectedApplication.country}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Occupation:</span>
                      <span className="value">{selectedApplication.occupation}</span>
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div className="detail-section">
                  <h3>Business Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Category:</span>
                      <span className="value">{selectedApplication.category}</span>
                    </div>
                    <div className="detail-item full-width">
                      <span className="label">Business Idea:</span>
                      <span className="value idea-text">{selectedApplication.idea}</span>
                    </div>
                  </div>
                </div>

                {/* Application Status */}
                <div className="detail-section">
                  <h3>Application Status</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Status:</span>
                      <span className={`value status-badge ${selectedApplication.status}`}>
                        {getStatusIcon(selectedApplication.status)}
                        {selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Submitted:</span>
                      <span className="value">{new Date(selectedApplication.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Last Updated:</span>
                      <span className="value">{new Date(selectedApplication.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="detail-section">
                  <h3>Payment Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Payment Status:</span>
                      <span className={`value payment-badge ${selectedApplication.payment_status || 'none'}`}>
                        {selectedApplication.payment_status || 'No Payment'}
                      </span>
                    </div>
                    {selectedApplication.payment_amount && (
                      <div className="detail-item">
                        <span className="label">Amount:</span>
                        <span className="value">₹{selectedApplication.payment_amount}</span>
                      </div>
                    )}
                    {selectedApplication.razorpay_payment_id && (
                      <div className="detail-item">
                        <span className="label">Payment ID:</span>
                        <span className="value">{selectedApplication.razorpay_payment_id}</span>
                      </div>
                    )}
                  </div>
                </div>

                                 {/* Uploaded Files */}
                 <div className="detail-section">
                   <h3>Uploaded Documents</h3>
                   <div className="files-grid">
                     {selectedApplication.video_file && (
                       <div className="file-item">
                         <div className="file-icon">
                           <FileText size={24} />
                         </div>
                         <div className="file-info">
                           <span className="file-name">Video Presentation</span>
                           <span className="file-type">MP4/Video File</span>
                         </div>
                         <div className="file-actions">
                           <button 
                             className="view-btn"
                             onClick={() => handleVideoView(selectedApplication.video_file)}
                             title="View Video"
                           >
                             <Eye size={16} />
                             View
                           </button>
                           <a 
                             href={`${API_BASE_URL}/files/download/${selectedApplication.video_file}`}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="download-btn"
                           >
                             Download
                           </a>
                         </div>
                       </div>
                     )}
                     {selectedApplication.pdf_file && (
                       <div className="file-item">
                         <div className="file-icon">
                           <FileText size={24} />
                         </div>
                         <div className="file-info">
                           <span className="file-name">Business Plan</span>
                           <span className="file-type">PDF Document</span>
                         </div>
                         <div className="file-actions">
                           <button 
                             className="view-btn"
                             onClick={() => handlePdfView(selectedApplication.pdf_file)}
                             title="View PDF"
                           >
                             <Eye size={16} />
                             View
                           </button>
                           <a 
                             href={`${API_BASE_URL}/files/download/${selectedApplication.pdf_file}`}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="download-btn"
                           >
                             Download
                           </a>
                         </div>
                       </div>
                     )}
                     {!selectedApplication.video_file && !selectedApplication.pdf_file && (
                       <div className="no-files">
                         <FileText size={48} />
                         <p>No files uploaded</p>
                       </div>
                     )}
                   </div>
                 </div>

                {/* Admin Notes */}
                <div className="detail-section">
                  <h3>Admin Notes</h3>
                  <div className="admin-notes">
                    <textarea
                      value={statusUpdateForm.admin_notes}
                      onChange={(e) => setStatusUpdateForm({...statusUpdateForm, admin_notes: e.target.value})}
                      placeholder="Add admin notes..."
                      rows={3}
                    />
                  </div>
                </div>

                {/* Status Update */}
                <div className="detail-section">
                  <h3>Update Status</h3>
                  <div className="status-update-form">
                    <select
                      value={statusUpdateForm.status}
                      onChange={(e) => setStatusUpdateForm({...statusUpdateForm, status: e.target.value})}
                    >
                      <option value="">Select Status</option>
                      <option value="pending">Pending</option>
                      <option value="reviewing">Reviewing</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <button
                      className="btn-primary"
                      onClick={() => updateApplicationStatus(
                        selectedApplication.id, 
                        statusUpdateForm.status, 
                        statusUpdateForm.admin_notes
                      )}
                      disabled={!statusUpdateForm.status}
                    >
                      Update Status
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {showVideoModal && selectedVideo && (
        <div className="modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h2>Video Presentation</h2>
              <button className="modal-close" onClick={closeVideoModal}>
                <X size={24} />
              </button>
            </div>
            
            <div className="video-modal-body">
              <video 
                controls 
                className="video-player"
                src={`${API_BASE_URL}/files/stream/${selectedVideo}`}
                preload="metadata"
                onError={handleVideoError}
                crossOrigin="anonymous"
              >
                Your browser does not support the video tag.
                <p>If you're seeing this message, the video format may not be supported by your browser.</p>
                <p>Try downloading the video file instead.</p>
              </video>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {showPdfModal && selectedPdf && (
        <div className="modal-overlay" onClick={closePdfModal}>
          <div className="pdf-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <h2>Business Plan PDF</h2>
              <button className="modal-close" onClick={closePdfModal}>
                <X size={24} />
              </button>
            </div>
            
            <div className="pdf-modal-body">
              <iframe 
                src={`${API_BASE_URL}/files/download/${selectedPdf}`}
                className="pdf-viewer"
                title="PDF Viewer"
                frameBorder="0"
              >
                <p>Your browser does not support iframes. 
                  <a href={`${API_BASE_URL}/files/download/${selectedPdf}`} target="_blank" rel="noopener noreferrer">
                    Click here to view the PDF
                  </a>
                </p>
              </iframe>
            </div>
          </div>
        </div>
      )}

      {/* User Management Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={closeUserModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isEditingUser ? 'Edit User' : 'Add New User'}</h3>
              <button className="close-btn" onClick={closeUserModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUserFormSubmit}>
                <div className="form-group">
                  <label htmlFor="userName">Name *</label>
                  <input
                    type="text"
                    id="userName"
                    value={userForm.name}
                    onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                    className={userFormErrors.name ? 'error' : ''}
                  />
                  {userFormErrors.name && <span className="error-text">{userFormErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="userEmail">Email *</label>
                  <input
                    type="email"
                    id="userEmail"
                    value={userForm.email}
                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    className={userFormErrors.email ? 'error' : ''}
                  />
                  {userFormErrors.email && <span className="error-text">{userFormErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="userPhone">Phone *</label>
                  <input
                    type="tel"
                    id="userPhone"
                    value={userForm.phone}
                    onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                    className={userFormErrors.phone ? 'error' : ''}
                  />
                  {userFormErrors.phone && <span className="error-text">{userFormErrors.phone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="userRole">Role</label>
                  <select
                    id="userRole"
                    value={userForm.role}
                    onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>



                <div className="form-group">
                  <label htmlFor="userStatus">Status</label>
                  <select
                    id="userStatus"
                    value={userForm.status}
                    onChange={(e) => setUserForm({...userForm, status: e.target.value})}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={closeUserModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {isEditingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}



    </div>
  );
};

export default AdminPanel; 